<template>
  <div class="retail-sale-page">
    <PosLayout
      :orders="orders"
      :active-order-id="activeOrderId"
      :exchange-rates="exchangeRates"
      :refresh-disabled="paymentSaving"
      @refresh="confirmRefresh"
      @change-order="changeOrder"
      @add-order="addOrder"
      @remove-order="removeOrder"
      @close-shift="openCloseShift"
    >
      <template #products>
        <div class="product-workspace">
          <div class="product-toolbar">
            <SearchControl
              :value="activeOrder.search"
              :scan-enabled="!editingItem && !paymentVisible && !closeShiftVisible && !setupIssueVisible && !expiredBarcodeDate"
              :pending-count="pendingScanCount"
              :feedback="scanFeedback"
              @input="updateSearch"
              @search="searchProducts"
              @scan="scanBarcode"
              @clear="clearSearch"
              @dismiss-scan-feedback="clearScanFeedback"
            />
          </div>

          <ProductCategoryTabs
            :categories="productCategories"
            :total-count="totalProductCount"
            :active-category-id="activeOrder.activeCategoryId"
            @select="selectCategory"
            @ready="markPagePartReady('categories')"
          />

          <div
            class="product-grid"
            :class="{ 'is-empty': !visibleProducts.length }"
            @scroll.passive="loadMoreProducts"
          >
            <template v-if="visibleProducts.length">
              <ProductCard
                v-for="product in visibleProducts"
                :key="product.id"
                :product="product"
                :currency="currency"
                @add="addProductToActiveOrder"
              />
            </template>

            <div v-else class="product-empty-state">
              <div class="product-empty-icon">
                <i class="fas fa-box-open"></i>
              </div>
              <strong>{{ $t('retailSale.product.emptyTitle') }}</strong>
              <p>{{ $t('retailSale.product.emptyHint') }}</p>
              <button
                v-if="activeOrder.search || activeOrder.activeCategoryId !== null"
                type="button"
                @click="resetProductFilters"
              >
                <i class="fas fa-rotate-left"></i>
                {{ $t('retailSale.product.clearFilters') }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #cart>
        <CartWorkspace
          :order="activeOrder"
          :customer-name="activeCustomerName"
          :customers="customers"
          :selected-customer="activeCustomer"
          :item-count="activeItemCount"
          :subtotal="activeOrderTotal"
          :tax-total="activeTaxTotal"
          :grand-total="activeGrandTotal"
          :vat-rate="activeOrder.vat_rate"
          :currency="currency"
          :edit-visible="Boolean(editingItem)"
          :editing-item="editingItem"
          :edit-sale-codes="editSaleCodes"
          :edit-loading="editLoading"
          :payment-visible="paymentVisible"
          :payment-resources="paymentResources"
          :payment-currencies="paymentCurrencies"
          :exchange-rates="exchangeRates"
          :payment-loading="paymentLoading"
          :payment-saving="paymentSaving"
          :payment-validation-errors="paymentValidationErrors"
          :promotion-choices="activePromotionChoices"
          :pending-scan-count="pendingScanCount"
          @decrease-item="decreaseItem"
          @increase-item="increaseItem"
          @remove-item="removeItem"
          @quantity-change-item="setItemQuantity"
          @select-free-item="selectFreeItem"
          @select-customer="selectCustomer"
          @edit-item="editItem"
          @cancel-edit-item="cancelEditItem"
          @remove-edit-item="removeEditItem"
          @save-edit-item="saveEditItem"
          @update-order-notes="updateOrderNotes"
          @complete-payment="openPaymentModal"
          @cancel-payment="closePaymentModal"
          @finish-payment="finishPayment"
          @preview-receipt="previewReceipt"
          @discount-change="updateInvoiceDiscount"
        />
      </template>
    </PosLayout>

    <PageLoadingMask :loading="pageLoading" :message="pageLoadingMessage" />

    <RetailSetupIssueModal
      :visible="setupIssueVisible"
      :missing-items="missingSetupItems"
      @back="goBack"
    />

    <RetailExpiredBarcodeModal
      :visible="Boolean(expiredBarcodeDate)"
      :expiry-date="expiredBarcodeDate"
      @close="expiredBarcodeDate = ''"
    />

    <RetailSaleShiftCloseModal
      :visible="closeShiftVisible"
      :currency-code="currency.currency_code || ''"
      :currencies="paymentCurrencies"
      :requires-password="closeShiftRequiresPassword"
      @cancel="closeShiftVisible = false"
      @closed="handleShiftClosed"
      @error="handleShiftError"
    />
  </div>
</template>

<script>
import { calculateCartItemAmounts, createCartItem } from '../utils/cart'
import {
  createRetailOrder,
  calculateRetailOrderTotals,
  buildRetailSalePayload,
  getDefaultFxRate,
  getDefaultFxRateRatio
} from '../utils/order'
import { getPromotionChoices } from '../utils/promotions'
import { isRetailScanExpired, parseRetailScan } from '../utils/product'
import {
  countCategoryProducts,
  createPaymentIdempotencyKey,
  findOrderCustomer,
  getNextOrderId,
  getOrderAmount,
  getOrderItemCount,
  shouldLoadMoreProducts,
  loadRetailSaleDrafts,
  saveRetailSaleDrafts,
  clearRetailSaleDrafts,
  createRetailSaleBroadcastChannel
} from '../utils/retail-sale'
import PageOptions from '@/config/PageOptions.vue'
import CartWorkspace from '../components/cart/CartWorkspace.vue'
import ProductCategoryTabs from '../components/product/ProductCategoryTabs.vue'
import ProductCard from '../components/product/ProductCard.vue'
import RetailSetupIssueModal from '../components/shared/RetailSetupIssueModal.vue'
import RetailExpiredBarcodeModal from '../components/shared/RetailExpiredBarcodeModal.vue'
import RetailSaleShiftCloseModal from '../components/shift/RetailSaleShiftCloseModal.vue'
import SearchControl from '../components/product/SearchControl.vue'
import PageLoadingMask from '../components/shared/PageLoadingMask.vue'
import PosLayout from '../layouts/PosLayout.vue'
import retailSaleDataService from '../services/retail-sale-data.service'
import {
  previewRetailSaleReceipt,
  printRetailSaleReceiptAfterSave,
  printRetailSaleShiftReport
} from '../services/retail-sale-print.service'

const BARCODE_CACHE_MS = 30000

export default {
  name: 'RetailSaleIndex',
  components: {
    CartWorkspace,
    ProductCategoryTabs,
    ProductCard,
    RetailSetupIssueModal,
    RetailExpiredBarcodeModal,
    RetailSaleShiftCloseModal,
    SearchControl,
    PageLoadingMask,
    PosLayout
  },
  data() {
    return {
      activeOrderId: 1,
      currency: {},
      paymentCurrencies: [],
      counters: [],
      countersReady: false,
      customers: [],
      customersReady: false,
      generalCustomer: null,
      exchangeRates: [],
      exchangeRatesReady: false,
      vatRate: 0,
      productCategories: [],
      defaultProducts: [],
      defaultProductsPage: 0,
      defaultProductsHasMore: false,
      requiredPageParts: ['currency', 'categories', 'products'],
      readyPageParts: [],
      orders: [createRetailOrder(1)],
      editingItem: null,
      editSaleCodes: [],
      editLoading: false,
      scanLoading: false,
      pendingScans: [],
      barcodeProductCache: new Map(),
      scanFeedback: null,
      expiredBarcodeDate: '',
      scanFeedbackTimer: null,
      paymentVisible: false,
      paymentLoading: false,
      paymentSaving: false,
      paymentValidationErrors: {},
      paymentIdempotencyKey: null,
      closeShiftVisible: false,
      closeShiftRequiresPassword: false,
      paymentResources: { payment_methods: [], banks: [] },
      promotions: [],
      promotionReady: false,
      promotionLoadFailed: false,
      draftsRestored: false,
      draftPersistTimer: null,
      broadcastChannel: null
    }
  },
  created() {
    this.restoreSavedDrafts()
    this.fetchCurrency()
    this.fetchCounters()
    this.fetchCustomers()
    this.fetchExchangeRates()
    this.fetchSettings()
    this.fetchProductCategories()
    this.fetchProducts()
    this.fetchPromotions()
    this.applyOrderContext()
    this.broadcastChannel = createRetailSaleBroadcastChannel()
  },
  beforeDestroy() {
    if (this.draftPersistTimer) clearTimeout(this.draftPersistTimer)
    if (this.scanFeedbackTimer) clearTimeout(this.scanFeedbackTimer)
    if (this.broadcastChannel) this.broadcastChannel.close()
  },
  watch: {
    orders: {
      deep: true,
      handler() {
        this.scheduleDraftPersistence()
      }
    },
    activeOrderId() {
      this.scheduleDraftPersistence()
    }
  },
  methods: {
    restoreSavedDrafts() {
      const saved = loadRetailSaleDrafts()
      if (!saved.orders.length) {
        this.draftsRestored = true
        return
      }

      this.orders = saved.orders.map(savedOrder => ({
        ...createRetailOrder(savedOrder.id, savedOrder.customer_id),
        ...savedOrder,
        productsLoading: false
      }))
      this.activeOrderId = saved.orders.some(
        order => order.id === saved.activeOrderId
      )
        ? saved.activeOrderId
        : saved.orders[0].id
      this.draftsRestored = true
    },
    scheduleDraftPersistence() {
      if (!this.draftsRestored) return
      if (this.draftPersistTimer) clearTimeout(this.draftPersistTimer)

      this.draftPersistTimer = setTimeout(() => {
        saveRetailSaleDrafts(this.orders, this.activeOrderId)
        this.broadcastChannel && this.broadcastChannel.postMessage({
          type: 'order-updated',
          activeOrderId: this.activeOrderId
        })
        this.draftPersistTimer = null
      }, 150)
    },
    async fetchCounters() {
      try {
        this.counters = await retailSaleDataService.fetchCounters()
      } catch (error) {
        this.counters = []
      } finally {
        this.countersReady = true
      }
    },
    async fetchExchangeRates() {
      try {
        this.exchangeRates = await retailSaleDataService.fetchExchangeRates()
        this.applyOrderContext()
      } catch (error) {
        this.exchangeRates = []
      } finally {
        this.exchangeRatesReady = true
      }
    },
    async fetchSettings() {
      try {
        const settings = await retailSaleDataService.fetchSettings()
        this.vatRate = Number(settings.vat_rate) || 0
        this.closeShiftRequiresPassword = Boolean(settings.is_close_shift_require_pwd)
        this.applyOrderContext()
      } catch (error) {
        this.vatRate = 0
      }
    },
    async fetchPromotions() {
      try {
        this.promotions = await retailSaleDataService.fetchPromotions()
        this.promotionLoadFailed = false
        this.orders.forEach(order => this.syncOrderTotals(order))
      } catch (error) {
        this.promotions = []
        this.promotionLoadFailed = true
      } finally {
        this.promotionReady = true
      }
    },
    async fetchCustomers() {
      try {
        const result = await retailSaleDataService.fetchCustomers()
        this.customers = result.customers
        this.generalCustomer = result.generalCustomer
        this.orders.forEach(order => {
          if (!order.customer_id) {
            order.customer_id = this.generalCustomer
              ? this.generalCustomer.customer_id
              : null
          }
        })
      } catch (error) {
        this.customers = []
        this.generalCustomer = null
      } finally {
        this.customersReady = true
      }
    },
    async fetchCurrency() {
      this.$store.dispatch('retailSale/setPageLoading', { loading: true })

      try {
        const result = await retailSaleDataService.fetchCurrency()
        this.currency = result.currency
        this.paymentCurrencies = result.currencies
      } catch (error) {
        this.currency = {}
        this.paymentCurrencies = []
      } finally {
        this.markPagePartReady('currency')
      }
    },
    async fetchProductCategories() {
      this.$store.dispatch('retailSale/setPageLoading', { loading: true })

      try {
        this.productCategories =
          await retailSaleDataService.fetchProductCategories()
      } catch (error) {
        this.productCategories = []
        this.markPagePartReady('categories')
      }
    },
    async fetchProducts(order = this.activeOrder, append = false) {
      if (!order || order.productsLoading) return

      order.productsLoading = true
      this.$store.dispatch('retailSale/setPageLoading', { loading: true })

      if (!append) {
        order.productsPage = 0
        order.productsHasMore = true
      }

      const page = append ? order.productsPage + 1 : 1
      try {
        const { params, products, pagination } =
          await retailSaleDataService.fetchProducts(order, page)

        order.products = append ? order.products.concat(products) : products
        order.productsPage = pagination.currentPage
        order.productsHasMore = pagination.hasMore

        if (!append && !params.search && order.activeCategoryId === null) {
          this.defaultProducts = products
          this.defaultProductsPage = order.productsPage
          this.defaultProductsHasMore = order.productsHasMore
        }
      } catch (error) {
        if (!append) order.products = []
      } finally {
        this.completeProductLoading()
        order.productsLoading = false
      }
    },
    completeProductLoading() {
      if (this.readyPageParts.includes('products')) {
        this.finishPageLoading()
        return
      }

      this.markPagePartReady('products')
    },
    markPagePartReady(part) {
      if (this.readyPageParts.includes(part)) return

      this.readyPageParts.push(part)

      if (
        this.requiredPageParts.every(part => this.readyPageParts.includes(part))
      ) {
        this.finishPageLoading()
      }
    },
    finishPageLoading() {
      this.$store.dispatch('retailSale/setPageLoading', { loading: false })
    },
    confirmRefresh() {
      if (this.paymentSaving) return

      this.$confirm({
        centered: true,
        title: this.$t('retailSale.header.confirmRefreshTitle'),
        content: this.$t('retailSale.header.confirmRefresh'),
        okText: this.$t('retailSale.header.refresh'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          this.draftsRestored = false
          if (this.draftPersistTimer) {
            clearTimeout(this.draftPersistTimer)
            this.draftPersistTimer = null
          }
          clearRetailSaleDrafts()
          if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({ type: 'order-updated' })
          }
          window.location.reload()
        }
      })
    },
    changeOrder(orderId) {
      this.saveProductGridState()
      this.activeOrderId = orderId
      this.restoreProductGridState()
    },
    updateSearch(search) {
      this.activeOrder.search = search
    },
    searchProducts(search) {
      this.activeOrder.search = search
      this.fetchProducts()
    },
    scanBarcode(barcode) {
      if (this.editingItem || this.paymentVisible || this.closeShiftVisible || this.setupIssueVisible || this.expiredBarcodeDate) return
      this.clearScanFeedback()
      const scan = parseRetailScan(barcode)
      if (!scan) {
        const code = String(barcode).trim()
        this.scanFeedback = {
          type: 'error',
          key: 'retailSale.search.unsupportedBarcode',
          params: { code, length: code.length }
        }
        return
      }
      if (isRetailScanExpired(scan.expiryDate)) {
        const date = scan.expiryDate
        this.expiredBarcodeDate = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
        return
      }

      this.pendingScans.push({ scan, orderId: this.activeOrderId })
      this.processScanQueue()
    },
    async processScanQueue() {
      if (this.scanLoading) return

      this.scanLoading = true
      try {
        while (this.pendingScans.length) {
          const { scan, orderId } = this.pendingScans.shift()
          try {
            const product = await this.fetchScannedProduct(scan)
            if (
              this.activeOrderId !== orderId ||
              this.paymentVisible ||
              this.editingItem ||
              this.closeShiftVisible ||
              this.setupIssueVisible
            ) continue
            const quantity = scan.kilograms === null ? 1 : scan.kilograms
            this.addProductToActiveOrder(product, quantity)
            const isKhmer = this.$i18n && this.$i18n.locale === 'kh'
            const name = isKhmer ? product.nameKh || product.name : product.name || product.nameKh
            this.showScanSuccess(name, quantity)
            if (this.activeOrder.search === scan.raw) {
              this.activeOrder.search = ''
              this.fetchProducts()
            }
          } catch (error) {
            this.clearScanFeedback()
            this.scanFeedback = {
              type: 'error',
              key: error && error.status === 409
                ? 'retailSale.search.ambiguousScaleBarcode'
                : 'retailSale.search.productNotFound'
            }
          }
        }
      } finally {
        this.scanLoading = false
      }
    },
    /** Reuse a recent successful barcode lookup within this POS page. */
    async fetchScannedProduct(scan) {
      const scale = scan.kilograms !== null
      const key = `${scale ? 'scale' : 'regular'}:${scan.barcode}`
      const cached = this.barcodeProductCache.get(key)
      if (cached && cached.expiresAt > Date.now()) return { ...cached.product }

      this.barcodeProductCache.delete(key)
      const product = await retailSaleDataService.fetchProductByBarcode(scan.barcode, scale)
      if (product) {
        this.barcodeProductCache.set(key, {
          product,
          expiresAt: Date.now() + BARCODE_CACHE_MS
        })
      }
      return product ? { ...product } : product
    },
    clearScanFeedback() {
      if (this.scanFeedbackTimer) clearTimeout(this.scanFeedbackTimer)
      this.scanFeedbackTimer = null
      this.scanFeedback = null
    },
    showScanSuccess(name, quantity) {
      this.clearScanFeedback()
      this.scanFeedback = {
        type: 'success',
        key: 'retailSale.search.scanAdded',
        params: { name, quantity }
      }
      this.scanFeedbackTimer = setTimeout(() => {
        this.scanFeedback = null
        this.scanFeedbackTimer = null
      }, 3000)
    },
    clearSearch() {
      this.activeOrder.search = ''
      this.fetchProducts()
    },
    selectCategory(categoryId) {
      this.activeOrder.activeCategoryId = categoryId
      this.activeOrder.scrollTop = 0
      this.fetchProducts()
    },
    resetProductFilters() {
      this.activeOrder.search = ''
      this.activeOrder.activeCategoryId = null
      this.activeOrder.scrollTop = 0
      this.fetchProducts()
    },
    loadMoreProducts(event) {
      const grid = event.currentTarget

      if (shouldLoadMoreProducts(this.activeOrder, grid)) {
        this.fetchProducts(this.activeOrder, true)
      }
    },
    saveProductGridState() {
      const grid = this.$el.querySelector('.product-grid')

      if (grid && this.activeOrder) {
        this.activeOrder.scrollTop = grid.scrollTop
      }
    },
    restoreProductGridState() {
      this.$nextTick(() => {
        const grid = this.$el.querySelector('.product-grid')

        if (grid && this.activeOrder) {
          grid.scrollTop = this.activeOrder.scrollTop
        }
      })
    },
    createOrderState(id) {
      const order = createRetailOrder(
        id,
        this.generalCustomer ? this.generalCustomer.customer_id : null
      )
      this.applyOrderContext(order)
      order.products = this.defaultProducts.slice()
      order.productsPage = this.defaultProductsPage
      order.productsHasMore = this.defaultProductsHasMore

      return order
    },
    addOrder() {
      const nextId = this.nextOrderId
      this.orders.push(this.createOrderState(nextId))
      this.changeOrder(nextId)
    },
    completeOrder(orderId) {
      if (this.orders.length > 1) {
        this.removeOrder(orderId, false)
        return
      }

      this.orders = [this.createOrderState(orderId)]
      this.activeOrderId = orderId
      this.restoreProductGridState()
    },
    selectCustomer(customer) {
      if (!customer || !this.activeOrder) return

      this.activeOrder.customer_id = customer.customer_id
    },
    applyOrderContext(order = null) {
      const orders = order ? [order] : this.orders
      const user = (this.$store.state.authUser || {}).user || {}
      const defaultLocation = user.default_location || {}
      const fxRate = getDefaultFxRate(this.exchangeRates)
      const fxRateRatio = getDefaultFxRateRatio(this.exchangeRates)

      orders.forEach(currentOrder => {
        currentOrder.location_id = defaultLocation.location_id || null
        currentOrder.fx_rate = fxRate
        currentOrder.fx_rate_ratio = fxRateRatio
        currentOrder.vat_rate = this.vatRate
        this.syncOrderTotals(currentOrder)
      })
    },
    syncOrderTotals(order = this.activeOrder) {
      if (!order) return

      Object.assign(order, calculateRetailOrderTotals(order, this.promotions))
    },
    addProductToActiveOrder(product, quantity = 1) {
      const existingItem = this.activeOrder.cartItems.find(
        item => item.product_id === product.id && !item.is_free_promotion_item
      )

      if (existingItem) {
        existingItem.quantity = Number(existingItem.quantity) + quantity
        Object.assign(existingItem, calculateCartItemAmounts(existingItem))
        const existingIndex = this.activeOrder.cartItems.indexOf(existingItem)
        if (existingIndex > 0) {
          this.activeOrder.cartItems.splice(existingIndex, 1)
          this.activeOrder.cartItems.unshift(existingItem)
        }
        this.syncOrderTotals()
        return
      }

      this.activeOrder.cartItems.unshift(createCartItem(product, quantity))
      this.syncOrderTotals()
    },
    decreaseItem(item) {
      if (item.is_free_promotion_item && !item.is_allow_adjust_free_quantity) return
      if (item.quantity > 1) {
        item.quantity -= 1
        if (item.is_free_promotion_item) {
          this.$set(this.activeOrder.promotion_quantities, item.cart_item_key, item.quantity)
        }
        Object.assign(item, calculateCartItemAmounts(item))
        this.syncOrderTotals()
        return
      }

      if (item.is_free_promotion_item) return

      this.activeOrder.cartItems = this.activeOrder.cartItems.filter(
        cartItem => cartItem.product_id !== item.product_id
      )
      this.syncOrderTotals()
    },
    increaseItem(item) {
      if (item.is_free_promotion_item && !item.is_allow_adjust_free_quantity) return
      item.quantity += 1
      if (item.is_free_promotion_item) {
        this.$set(this.activeOrder.promotion_quantities, item.cart_item_key, item.quantity)
      }
      Object.assign(item, calculateCartItemAmounts(item))
      this.syncOrderTotals()
    },
    setItemQuantity(payload) {
      if (!payload || !payload.item) return
      if (
        payload.item.is_free_promotion_item &&
        !payload.item.is_allow_adjust_free_quantity
      ) return

      const quantity = Number(payload.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return

      payload.item.quantity = quantity
      if (payload.item.is_free_promotion_item) {
        this.$set(
          this.activeOrder.promotion_quantities,
          payload.item.cart_item_key,
          quantity
        )
      }
      Object.assign(payload.item, calculateCartItemAmounts(payload.item))
      this.syncOrderTotals()
    },
    selectFreeItem(selection) {
      if (!selection || !selection.conditionId) return

      this.$set(
        this.activeOrder.promotion_selections,
        selection.conditionId,
        selection.saleCodeId || null
      )
      if (selection.promotionId && selection.saleCodeId && selection.quantity) {
        this.$set(
          this.activeOrder.promotion_quantities,
          `promotion-${selection.promotionId}-${selection.conditionId}-${selection.saleCodeId}`,
          Number(selection.quantity)
        )
      }
      this.syncOrderTotals()
    },
    updateInvoiceDiscount(discount) {
      if (!discount || !this.activeOrder) return

      this.activeOrder.invoice_discount_type = discount.discount_type
      this.activeOrder.invoice_discount_rate = Number(discount.discount_rate) || 0
      this.activeOrder.invoice_discount_amount = Number(discount.discount) || 0
      this.syncOrderTotals()
    },
    removeItem(item) {
      if (item.is_free_promotion_item) return
      this.activeOrder.cartItems = this.activeOrder.cartItems.filter(
        cartItem => cartItem.product_id !== item.product_id
      )
      this.syncOrderTotals()
    },
    async editItem(item) {
      this.editingItem = item
      this.editSaleCodes = []
      this.editLoading = true

      try {
        this.editSaleCodes = await retailSaleDataService.fetchProductSaleCodes(
          item.product_id
        )
      } catch (error) {
        this.editSaleCodes = [
          {
            pro_sale_code_id: item.pro_sale_code_id,
            product_id: item.product_id,
            uom_id: item.uom_id,
            uom_name_en: item.name,
            uom_name_kh: item.name,
            sale_code: item.sale_code,
            price: item.unit_price,
            stock: 0,
            is_default: true,
            promotion: item.promotion || null
          }
        ]
      } finally {
        this.editLoading = false
      }
    },
    cancelEditItem() {
      this.editingItem = null
      this.editSaleCodes = []
    },
    saveEditItem(updatedItem) {
      if (!updatedItem || !this.editingItem) return

      Object.assign(this.editingItem, updatedItem)
      this.syncOrderTotals()
      this.cancelEditItem()
    },
    updateOrderNotes(notes) {
      this.activeOrder.remarks = notes
    },
    async openPaymentModal() {
      if (this.pendingScanCount > 0) return

      this.paymentVisible = true
      this.paymentValidationErrors = {}

      if (
        this.paymentResources.payment_methods.length ||
        this.paymentLoading
      ) {
        return
      }

      this.paymentLoading = true

      try {
        this.paymentResources =
          await retailSaleDataService.fetchPaymentResources()
      } catch (error) {
        this.paymentResources = { payment_methods: [], banks: [] }
      } finally {
        this.paymentLoading = false
      }
    },
    closePaymentModal() {
      this.paymentVisible = false
      this.paymentValidationErrors = {}
      this.paymentIdempotencyKey = null
    },
    openCloseShift() {
      this.closeShiftVisible = true
    },
    async handleShiftClosed(result) {
      this.closeShiftVisible = false
      const summary = (result && result.summary) || {}
      const user = (this.$store.state.authUser || {}).user || {}
      const branch = user.default_branch || {}
      const company = branch.company || {}
      const locale = this.$i18n && this.$i18n.locale === 'kh' ? 'kh' : 'en'
      const branchName =
        locale === 'kh'
          ? branch.branch_name_kh || branch.branch_name_en || ''
          : branch.branch_name_en || branch.branch_name_kh || ''
      const companyName =
        locale === 'kh'
          ? company.company_name_kh || company.company_name_en || ''
          : company.company_name_en || company.company_name_kh || ''
      const currentCounter = this.counters.find(
        counter => Number(counter.counter_id) === Number(summary.counter_id)
      )
      const counterValue = currentCounter && currentCounter.counter_name
        ? currentCounter.counter_name
        : summary.counter_id
          ? `#${String(summary.counter_id).padStart(3, '0')}`
          : '#001'
      const reportPrinted = await printRetailSaleShiftReport({
        summary,
        cashier: user.name || user.user_name || '',
        counter: counterValue,
        locale,
        labels: {
          title: this.$t('retailSale.shiftReport.title'),
          dateTime: this.$t('retailSale.shiftReport.dateTime'),
          cashier: this.$t('retailSale.shiftReport.cashier'),
          counter: this.$t('retailSale.shiftReport.counter'),
          period: this.$t('retailSale.shiftReport.period'),
          invoiceDetails: this.$t('retailSale.shiftReport.invoiceDetails'),
          orderedItemsDetail: this.$t('retailSale.shiftReport.orderedItemsDetail'),
          item: this.$t('retailSale.shiftReport.item'),
          qty: this.$t('retailSale.shiftReport.qty'),
          price: this.$t('retailSale.shiftReport.price'),
          items: this.$t('retailSale.shiftReport.items'),
          units: this.$t('retailSale.shiftReport.units'),
          transactions: this.$t('retailSale.shiftReport.transactions'),
          currency: this.$t('retailSale.shiftReport.currency'),
          qtyPrice: this.$t('retailSale.shiftReport.qtyPrice'),
          noInvoices: this.$t('retailSale.shiftReport.noInvoices'),
          summary: this.$t('retailSale.shiftReport.summary'),
          subtotal: this.$t('retailSale.shiftReport.subtotal'),
          discount: this.$t('retailSale.shiftReport.discount'),
          tax: this.$t('retailSale.shiftReport.tax'),
          total: this.$t('retailSale.shiftReport.total'),
          netSales: this.$t('retailSale.shiftReport.netSales'),
          completedInvoices: this.$t('retailSale.shiftReport.completedInvoices'),
          tenderBreakdown: this.$t('retailSale.shiftReport.tenderBreakdown'),
          grossSales: this.$t('retailSale.shiftReport.grossSales'),
          discounts: this.$t('retailSale.shiftReport.discounts'),
          paymentBreakdown: this.$t('retailSale.shiftReport.paymentBreakdown'),
          closed: this.$t('retailSale.shiftReport.closed')
        },
        company: locale === 'kh'
          ? company.company_name_kh || company.company_name_en || ''
          : company.company_name_en || company.company_name_kh || '',
        branch: branchName
      }).catch(() => false)
      this.$toasted.success(this.$t('retailSale.shiftClose.success'))
      if (!reportPrinted) {
        this.$toasted.error(this.$t('retailSale.shiftReport.printError'))
      }
      await this.$store.dispatch('logout')
      this.$router.push({ name: 'login' })
    },
    handleShiftError(error) {
      const message = error && (error.message || error.errors)
      this.$toasted.error(
        typeof message === 'string' ? message : this.$t('retailSale.shiftClose.error')
      )
    },
    buildReceiptData(order, payment, savedSale = {}) {
        const locale = this.$i18n && this.$i18n.locale === 'kh' ? 'kh' : 'en'
        const user = (this.$store.state.authUser || {}).user || {}
        const branch = user.default_branch || {}
        const company = branch.company || {}
        const branchName =
          locale === 'kh'
            ? branch.branch_name_kh || branch.branch_name_en || ''
            : branch.branch_name_en || branch.branch_name_kh || ''
        const branchAddress = branch.branch_address || company.company_address || ''
        const branchPhone = branch.branch_phone || company.company_phone || ''
        const companyName =
          locale === 'kh'
            ? company.company_name_kh || company.company_name_en || this.$t('retailSale.receipt.storeName')
            : company.company_name_en || company.company_name_kh || this.$t('retailSale.receipt.storeName')
        const receiptData = {
          order,
          payment,
          savedSale,
          currency: this.currency,
          currencies: this.paymentCurrencies,
          paymentMethods: this.paymentResources.payment_methods,
          labels: {
            title: this.$t('retailSale.receipt.title'),
            storeName:
              companyName,
            branch: branchName,
            address: branchAddress,
            vatTin: this.$t('retailSale.receipt.vatTin'),
            phone: this.$t('retailSale.receipt.phone'),
            phoneValue: branchPhone,
            receiver: this.$t('retailSale.receipt.receiver'),
            operator: this.$t('retailSale.receipt.operator'),
            operatorValue: user.name || user.user_name || '',
            receiptNumber: this.$t('retailSale.receipt.receiptNumber'),
            type: this.$t('retailSale.receipt.type'),
            customerValue: this.activeCustomerName || '',
            time: this.$t('retailSale.receipt.time'),
            itemDescription: this.$t('retailSale.receipt.itemDescription'),
            qty: this.$t('retailSale.receipt.qty'),
            price: this.$t('retailSale.receipt.price'),
            itemDiscount: this.$t('retailSale.receipt.itemDiscount'),
            order: this.$t('retailSale.receipt.order'),
            customer: this.$t('retailSale.receipt.customer'),
            customerName: this.activeCustomerName,
            subtotal: this.$t('retailSale.receipt.subtotal'),
            discount: this.$t('retailSale.receipt.discount'),
            tax: this.$t('retailSale.receipt.tax'),
            total: this.$t('retailSale.receipt.total'),
            totalItemsScanned: this.$t('retailSale.receipt.totalItemsScanned'),
            grossSubtotal: this.$t('retailSale.receipt.grossSubtotal'),
            subtotalAfterItemDiscounts: this.$t('retailSale.receipt.subtotalAfterItemDiscounts'),
            invoiceDiscount: this.$t('retailSale.receipt.invoiceDiscount'),
            subtotalAfterSavings: this.$t('retailSale.receipt.subtotalAfterSavings'),
            serviceCharge: this.$t('retailSale.receipt.serviceCharge'),
            grandTotal: this.$t('retailSale.receipt.grandTotal'),
            totalInRiels: this.$t('retailSale.receipt.totalInRiels'),
            officialExchangeRate: this.$t('retailSale.receipt.officialExchangeRate'),
            tenderPaymentBreakdown: this.$t('retailSale.receipt.tenderPaymentBreakdown'),
            totalPaidTendered: this.$t('retailSale.receipt.totalPaidTendered'),
            changeDue: this.$t('retailSale.receipt.changeDue'),
            changeDueBase: this.$t('retailSale.receipt.changeDueBase'),
            changeDueKhr: this.$t('retailSale.receipt.changeDueKhr'),
            goodsPolicy: this.$t('retailSale.receipt.goodsPolicy'),
            lineNet: this.$t('retailSale.receipt.lineNet'),
            promotion: this.$t('retailSale.receipt.promotion'),
            free: this.$t('retailSale.receipt.free'),
            freeItem: this.$t('retailSale.receipt.freeItem'),
            payment: this.$t('retailSale.receipt.payment'),
            thankYou: this.$t('retailSale.receipt.thankYou'),
            unknownItem: this.$t('retailSale.receipt.unknownItem')
          },
          dateLabel: new Date().toLocaleString(
            locale === 'kh' ? 'km-KH' : 'en-US'
          ),
          locale
        }
        return receiptData
    },
    previewReceipt(payment) {
      if (process.env.NODE_ENV !== 'development') return
      const previewInvoiceNo = String(Date.now()).padStart(16, '0')
      const invoiceDiscount = Number(payment.discount || 0)
      const previewTotals = {
        invoice_discount_amount: invoiceDiscount,
        target_net_due: Math.max(Number(this.activeOrder.grand_total || 0) - invoiceDiscount, 0)
      }
      previewRetailSaleReceipt(
        this.buildReceiptData(this.activeOrder, payment, {
          invoice_no: previewInvoiceNo,
          totals: previewTotals
        })
      )
    },
    async finishPayment(payment) {
      if (this.paymentSaving) return

      this.paymentSaving = true
      const completedOrder = JSON.parse(JSON.stringify(this.activeOrder))
      if (!this.paymentIdempotencyKey) {
        this.paymentIdempotencyKey = createPaymentIdempotencyKey()
      }
      try {
        const savedSale = await retailSaleDataService.saveRetailSale(
          buildRetailSalePayload(this.activeOrder, payment),
          this.paymentIdempotencyKey
        )
        this.barcodeProductCache.clear()
        const receiptData = this.buildReceiptData(completedOrder, payment, savedSale)
        const printed = printRetailSaleReceiptAfterSave(receiptData)
        if (!printed) {
          this.$toasted.error(this.$t('retailSale.receipt.printBlocked'))
        }
        this.$toasted.success(
          this.$t('retailSale.paymentModal.paymentSuccess')
        )
        if (this.broadcastChannel) {
          this.broadcastChannel.postMessage({
            type: 'payment-success',
            order: completedOrder
          })
        }
        this.paymentValidationErrors = {}
        this.paymentIdempotencyKey = null
      } catch (error) {
        this.paymentValidationErrors =
          (error && error.errors) || {}
        const message =
          (error && (error.message || error.messages)) ||
          this.$t('retailSale.paymentModal.paymentError')
        this.$toasted.error(
          Array.isArray(message) ? message.join(', ') : String(message)
        )
        return
      } finally {
        this.paymentSaving = false
      }
      this.closePaymentModal()
      this.completeOrder(completedOrder.id)
      this.$emit('payment-ready', { order: completedOrder, payment })
    },
    removeEditItem(item) {
      this.removeItem(item)
      this.cancelEditItem()
    },
    removeOrder(orderId, shouldConfirm = true) {
      if (shouldConfirm) {
        this.$confirm({
          centered: true,
          title: this.$t('retailSale.orderTabs.confirmCloseTitle'),
          content: this.$t('retailSale.orderTabs.confirmClose'),
          okText: this.$t('yes'),
          okType: 'danger',
          cancelText: this.$t('no'),
          onOk: () => this.removeOrder(orderId, false)
        })
        return
      }

      if (this.orders.length === 1) {
        clearRetailSaleDrafts()
        this.orders = [this.createOrderState(orderId)]
        this.activeOrderId = orderId
        this.restoreProductGridState()
        return
      }

      const removedIndex = this.orders.findIndex(order => order.id === orderId)
      const wasActiveOrder = this.activeOrderId === orderId

      if (wasActiveOrder) {
        this.saveProductGridState()
      }

      this.orders = this.orders.filter(order => order.id !== orderId)

      if (!wasActiveOrder) {
        return
      }

      const nextActiveOrder =
        this.orders[removedIndex] ||
        this.orders[removedIndex - 1] ||
        this.orders[0]

      this.activeOrderId = nextActiveOrder.id
      this.restoreProductGridState()
    },
    goBack() {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.go(-1)
        return
      }

      this.$router.replace({ name: 'home' })
    }
  },
  computed: {
    pendingScanCount() {
      return this.pendingScans.length + (this.scanLoading ? 1 : 0)
    },
    pageLoading() {
      return this.$store.state.retailSale.pageLoading
    },
    pageLoadingMessage() {
      return this.$store.state.retailSale.pageLoadingMessage
    },
    missingSetupItems() {
      const missingItems = []

      if (!this.counters.length) {
        missingItems.push(this.$t('retailSale.setupIssue.counter'))
      }

      if (!this.customers.length) {
        missingItems.push(this.$t('retailSale.setupIssue.customer'))
      }

      if (!this.exchangeRates.length) {
        missingItems.push(this.$t('retailSale.setupIssue.exchangeRate'))
      }

      if (this.promotionLoadFailed) {
        missingItems.push(this.$t('retailSale.setupIssue.promotions'))
      }

      return missingItems
    },
    setupIssueVisible() {
      return (
        this.countersReady &&
        this.customersReady &&
        this.exchangeRatesReady &&
        this.promotionReady &&
        this.missingSetupItems.length > 0
      )
    },
    filteredProducts() {
      return this.activeOrder.products
    },
    totalProductCount() {
      return countCategoryProducts(this.productCategories)
    },
    visibleProducts() {
      return this.filteredProducts
    },
    activeOrder() {
      return (
        this.orders.find(order => order.id === this.activeOrderId) ||
        this.orders[0]
      )
    },
    activeCustomerName() {
      return this.activeCustomer ? this.activeCustomer.customer_name : ''
    },
    activeCustomer() {
      return findOrderCustomer(
        this.customers,
        this.activeOrder,
        this.generalCustomer
      )
    },
    activeOrderTotal() {
      return getOrderAmount(this.activeOrder, 'total_after_discount')
    },
    activeItemCount() {
      return getOrderItemCount(this.activeOrder)
    },
    activeTaxTotal() {
      return getOrderAmount(this.activeOrder, 'vat_amount')
    },
    activeGrandTotal() {
      return getOrderAmount(this.activeOrder, 'grand_total')
    },
    activePromotionChoices() {
      return getPromotionChoices(this.activeOrder, this.promotions)
    },
    nextOrderId() {
      return getNextOrderId(this.orders)
    }
  },
  beforeRouteLeave(to, from, next) {
    PageOptions.pageEmpty = false
    next()
  }
}
</script>

<style scoped src="../styles/index.css"></style>

<style>
.retail-sale-page input[type='number']::-webkit-inner-spin-button,
.retail-sale-page input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.retail-sale-page input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
